import { isDefined } from "@clipboard-health/util-ts";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { SUPPORT_WEBFORM_URL } from "@src/appV2/ContactCBHInfo/constants";
import { ExternalLink } from "@src/appV2/lib/Links/ExternalLink";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { useCreateWorkplaceAgreement } from "@src/appV2/WorkplaceAgreement/api/useCreateWorkplaceAgreement";
import { useGetWorkplaceAgreements } from "@src/appV2/WorkplaceAgreement/api/useGetWorkplaceAgreements";
import { LoadingButton, Text } from "@src/lib/ui-react";
import { isEmpty } from "lodash";
import { useState } from "react";

const TERMS_OF_SERVICE_URL = "https://www.clipboardworks.com/terms-of-service";
const PRIVACY_POLICY_URL = "https://www.clipboardworks.com/privacy-policy";

/**
 * "per-user" variation: prompts each workplace user (not once per workplace) until they personally
 * accept, gating on `GET .../agreements?scope=user` with no first-shift delay.
 */
export function WorkplaceAgreementPerUserDialog() {
  const session = useSession();
  const { admin: isAdmin, profile, user } = session ?? { profile: undefined, user: undefined };
  const workplaceId = profile?.userId;
  const userId = user?._id ?? "";

  const isWorkplaceUser = isDefined(workplaceId) && !isAdmin && !isEmpty(userId);

  const {
    data: workplaceAgreements,
    isLoading: isLoadingWorkplaceAgreements,
    isError: isErrorGettingWorkplaceAgreements,
  } = useGetWorkplaceAgreements(workplaceId, { scope: "user" }, { enabled: isWorkplaceUser });

  const { mutateAsync: createWorkplaceAgreement, isLoading: isLoadingCreateWorkplaceAgreement } =
    useCreateWorkplaceAgreement(workplaceId ?? "");

  const [visible, setVisible] = useState<boolean>(true);

  const onAgree = async () => {
    try {
      await createWorkplaceAgreement({
        workplaceId: workplaceId ?? "",
        acceptedByUserId: userId,
        acceptedAt: new Date(),
        arbitrationAgreementVersionNumber: 1,
      });
      setVisible(false);
    } catch {
      showErrorToast("Failed to accept agreement. Please try again or contact support.");
    }
  };

  if (!isWorkplaceUser) {
    return null;
  }

  if (
    isErrorGettingWorkplaceAgreements ||
    isLoadingWorkplaceAgreements ||
    !isEmpty(workplaceAgreements?.data)
  ) {
    return null;
  }

  return (
    <Dialog disableEscapeKeyDown fullWidth open={visible} maxWidth="sm">
      <DialogTitle sx={{ textAlign: "center" }}>Terms of Service &amp; Privacy Policy</DialogTitle>
      <DialogContent>
        <Stack spacing={3} alignItems="center" textAlign="center" sx={{ pt: 2 }}>
          <Text variant="body1">
            Please carefully review the documents below. By clicking &apos;AGREE AND ACCEPT,&apos;
            you confirm that you have read, understood, and agree to be bound by Clipboard&apos;s
            Terms of Service, and acknowledge that you have read our Privacy Policy.
          </Text>
          <Stack spacing={1} alignItems="center" textAlign="center">
            <ExternalLink
              to={TERMS_OF_SERVICE_URL}
              underline="always"
              sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
            >
              Terms of Service
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </ExternalLink>
            <ExternalLink
              to={PRIVACY_POLICY_URL}
              underline="always"
              sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
            >
              Privacy Policy
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </ExternalLink>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
        <Stack spacing={1} alignItems="center" textAlign="center">
          <LoadingButton
            variant="contained"
            size="large"
            sx={{ textTransform: "uppercase", px: 4, borderRadius: 6 }}
            disabled={isLoadingCreateWorkplaceAgreement}
            isLoading={isLoadingCreateWorkplaceAgreement}
            onClick={onAgree}
          >
            AGREE AND ACCEPT
          </LoadingButton>
          <Text variant="body2">
            <ExternalLink to={SUPPORT_WEBFORM_URL} underline="hover">
              Contact Support
            </ExternalLink>
          </Text>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
